import {
  TxAction,
  TxStatus,
  type FormattedTransaction,
} from "@/interfaces/transaction";
import {
  ArrowDownRight,
  ArrowLeftRight,
  ArrowUpRight,
  CheckCircle2,
  Clock,
  XCircle,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  formatDate,
  formatEvmHumanBalance,
  normalizeCurrency,
  removeTrailingToken,
} from "@/utils";

const getTypeIcon = (tx: FormattedTransaction) => {
  if (tx?.action === "SWAP") {
    return <ArrowLeftRight className="h-6 w-6 text-violet-500" />;
  }

  if (tx?.direction === "IN") {
    return <ArrowDownRight className="h-6 w-6 text-emerald-500" />;
  }

  if (tx?.direction === "OUT") {
    return <ArrowUpRight className="h-6 w-6 text-rose-500" />;
  }

  return <ArrowLeftRight className="h-6 w-6 text-violet-500" />;
};

const getStatusBadge = (status: TxStatus) => {
  switch (status) {
    case "COMPLETED":
      return (
        <Badge className="bg-emerald-500/10 text-emerald-500 border-emerald-500/30">
          <CheckCircle2 className="h-4 w-4 mr-1" /> Completed
        </Badge>
      );
    case "PENDING":
      return (
        <Badge className="bg-amber-500/10 text-amber-500 border-amber-500/30">
          <Clock className="h-4 w-4 mr-1" /> Pending
        </Badge>
      );
    case "FAILED":
      return (
        <Badge className="bg-red-500/10 text-red-500 border-red-500/30">
          <XCircle className="h-4 w-4 mr-1" /> Failed
        </Badge>
      );
    default:
      return <Badge variant="outline">{status}</Badge>;
  }
};

export const TransactionRow = ({
  tx,
  onClick,
  onCopy,
}: {
  tx: FormattedTransaction;
  onClick: () => void;
  onCopy: (e: React.MouseEvent) => void;
}) => {
  return (
    <div
      onClick={onClick}
      className="group flex items-center gap-5 px-1 sm:px-5 py-4 hover:bg-muted/50 transition-colors cursor-pointer rounded-lg"
    >
      {/* Icon */}
      <div className="h-10 w-10 rounded-lg bg-muted/60 flex items-center justify-center border border-border/50 shrink-0">
        {getTypeIcon(tx)}
      </div>

      {/* Main */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-2">
          <p className="text-sm font-medium truncate">
            {tx?.counterparty?.name}
          </p>
        </div>

        <p className="text-sm text-muted-foreground">
          {tx?.message}:{" "}
          {tx.action === "SWAP" &&
            `From ${removeTrailingToken(tx?.amount?.currency)} ${formatEvmHumanBalance(tx?.amount?.value, tx?.asset?.token?.isNative)} to
           ${removeTrailingToken(tx?.metadata?.tokenTo ?? "")} ${formatEvmHumanBalance(Number(tx?.metadata?.amountTo), tx?.metadata?.isToNative ?? false)}`}
        </p>

        <p className="text-[11px] text-muted-foreground mt-1">
          {formatDate(tx?.timestamps?.createdAt)}
        </p>
      </div>

      {/* Amount */}
      <div className="text-right shrink-0 flex flex-col gap-2">
        <span>{getStatusBadge(tx?.status)}</span>
        <p
          className={`text-sm font-semibold tabular-nums ${
            tx?.direction === "IN" || tx?.action === "SWAP"
              ? "text-emerald-600"
              : "text-rose-600"
          }`}
        >
          {formatEvmHumanBalance(
            tx.action === TxAction.SWAP
              ? (tx.metadata?.amountTo ?? "0")
              : (tx?.amount?.value ?? "0"),
            tx?.asset?.token?.isNative,
          )}{" "}
          <span className="text-muted-foreground font-semibold">
            {tx.action === TxAction.SWAP
              ? normalizeCurrency(tx?.converted?.currency ?? "")
              : tx?.asset?.currency}
          </span>
        </p>
      </div>

      {/* Action */}
      {/* <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity ml-1"
        onClick={onCopy}
      >
        <Copy className="h-3.5 w-3.5" />
      </Button> */}
    </div>
  );
};
